import { APP_VERSION, IS_RUNNING_TESTS } from '../../common/constants';
import { ENV_TYPE_SHORTHAND } from '../../env-type';
import type { EvseVariant } from '../../features/ev-charger/evse-variant';
import { AWS_REGION, REGION_SHORTHAND } from '../../region-type';
import type { JourneyId, JourneyPriority, JourneyStep } from './dynatrace-journey';
import { trackDynatraceJourneyError, trackDynatraceJourneyEvent } from './dynatrace-journey';
import {
  clearJourneyTimer,
  ensureJourneyTimerStarted,
  getJourneyElapsedMs,
  startJourneyTimer,
} from './dynatrace-journey-timer';

type DynatraceEventOptions = {
  journeyId: JourneyId;
  journeyStep: JourneyStep;
  eventName: string;
  priority?: JourneyPriority;
  outcome?: 'success' | 'failure';
  attributes?: Record<string, string | number | boolean | undefined>;
};

type DynatraceErrorOptions = Omit<DynatraceEventOptions, 'outcome'> & {
  errorCode: string;
  errorMessage?: string;
};

const DYNATRACE_ERROR_CODES = new Set([
  'auth_session_unavailable',
  'external_idp_tokens_missing',
  'offline_setup_error',
  'schedule_config_error',
  'schedule_api_failed',
  'stop_charging_failed',
  'start_charging_failed',
  'add_evse_to_site_failed',
  'evse_variant_detection_failed',
  'evse_model_sync_failed',
  'unknown_error',
]);

const OAUTH_CALLBACK_ERROR_CODES = new Set([
  'access_denied',
  'interaction_required',
  'invalid_client',
  'invalid_request',
  'invalid_scope',
  'login_required',
  'server_error',
  'temporarily_unavailable',
  'unauthorized_client',
  'unsupported_response_type',
]);

function withCommonAttributes(journeyId: JourneyId, attributes: DynatraceEventOptions['attributes'] = {}) {
  return {
    app_version: APP_VERSION,
    elapsed_ms_since_start: getJourneyElapsedMs(journeyId),
    ...attributes,
  };
}

function normalizeDynatraceErrorCode(errorCode: string, fallback = 'unknown_error') {
  const normalized = errorCode
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  return normalized && DYNATRACE_ERROR_CODES.has(normalized) ? normalized : fallback;
}

function normalizeOAuthCallbackErrorCode(errorCode: string) {
  const normalized = errorCode.trim().toLowerCase();
  return OAUTH_CALLBACK_ERROR_CODES.has(normalized) ? normalized : 'unknown';
}

function sanitizeDynatraceErrorMessage(errorMessage?: string) {
  const sanitized = errorMessage?.replace(/\s+/g, ' ').trim();
  if (!sanitized) return undefined;

  const redacted = sanitized
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[email]')
    .replace(/\bAuthorization:\s+Bearer\s+[^\s]+/gi, 'Authorization: [redacted]')
    .replace(/\b(Bearer|Token)\s+[^\s]+/gi, '$1 [redacted]')
    .replace(/\b(Authorization|password|secret|api[-_]?key)(:|=)\s*[^\s]+/gi, (_match, key, separator) => {
      return separator === ':' ? `${key}: [redacted]` : `${key}=[redacted]`;
    })
    .replace(/https?:\/\/[^\s]+/gi, '[url]');

  return redacted.slice(0, 120);
}

function trackDynatraceEvent({
  journeyId,
  journeyStep,
  eventName,
  priority = 'standard',
  outcome = 'success',
  attributes,
}: DynatraceEventOptions) {
  if (IS_RUNNING_TESTS) return;

  trackDynatraceJourneyEvent({
    journeyId,
    journeyStep,
    eventName,
    outcome,
    priority,
    attributes: withCommonAttributes(journeyId, attributes),
  });
}

function trackDynatraceError({
  journeyId,
  journeyStep,
  eventName,
  priority = 'standard',
  errorCode,
  errorMessage,
  attributes,
}: DynatraceErrorOptions) {
  if (IS_RUNNING_TESTS) return;

  const safeErrorCode = normalizeDynatraceErrorCode(errorCode);
  const safeErrorMessage = sanitizeDynatraceErrorMessage(errorMessage);

  trackDynatraceJourneyError({
    journeyId,
    journeyStep,
    eventName,
    errorMessage: safeErrorCode,
    priority,
    attributes: withCommonAttributes(journeyId, {
      ...attributes,
      error_code: safeErrorCode,
      ...(safeErrorMessage ? { error_message: safeErrorMessage } : {}),
    }),
  });
}

// Most tracked functions are a fixed journeyId/journeyStep/eventName fired with no parameters
// (e.g. "screen opened", "flow started/skipped"). Factor those out so adding one is a one-liner.
function createDynatraceEventTracker(config: Omit<DynatraceEventOptions, 'outcome' | 'attributes'>) {
  return () => trackDynatraceEvent(config);
}

// Companion for events that only ever report a failure (no matching "success" tracker is exported),
// e.g. offline setup or schedule validation errors. Defaults errorMessage to errorCode when the
// caller has no more specific diagnostic to report.
function createDynatraceErrorTracker(config: Omit<DynatraceErrorOptions, 'attributes' | 'errorMessage'>) {
  return (errorMessage: string = config.errorCode) => trackDynatraceError({ ...config, errorMessage });
}

export type PermissionOutcome = 'granted' | 'denied';
export type ConsentEventSource = 'account_refresh' | 'settings';

export type DynatraceLiveStatusSource = 'sse' | 'native_poll';

export type DynatraceScheduleStatus = {
  isScheduledChargeActive?: boolean | null;
  scheduledChargeStartTime?: string | null;
  scheduledChargeEndTime?: string | null;
  controlMode?: 'Manual' | 'Automatic' | null;
};

function getLiveStatusAttributes(chargerId: string) {
  return { charger_id: chargerId };
}

export function trackDynatraceDeviceStatusOpened(chargerId: string) {
  trackDynatraceEvent({
    journeyId: 'live_status_awareness',
    journeyStep: 'status',
    eventName: 'charger_status_view_opened',
    attributes: getLiveStatusAttributes(chargerId),
  });
}

export function trackDynatraceSseLifecycle(
  eventName:
    | 'sse_connection_started'
    | 'sse_connected'
    | 'sse_reconnected'
    | 'sse_connection_failed'
    | 'sse_disconnected'
    | 'sse_silence_detected',
  chargerId: string,
  attributes: {
    reason?: 'stream_ended' | 'network_error' | 'silence_timeout';
    silenceTimeoutMs?: number;
    statusFreshnessMs?: number;
  } = {}
) {
  const outcome =
    eventName === 'sse_connection_failed' || eventName === 'sse_disconnected' || eventName === 'sse_silence_detected'
      ? 'failure'
      : 'success';
  trackDynatraceEvent({
    journeyId: 'live_status_awareness',
    journeyStep: 'status',
    eventName,
    outcome,
    attributes: {
      ...getLiveStatusAttributes(chargerId),
      ...(attributes.reason ? { reason: attributes.reason } : {}),
      ...(attributes.silenceTimeoutMs !== undefined ? { silence_timeout_ms: attributes.silenceTimeoutMs } : {}),
      ...(attributes.statusFreshnessMs !== undefined ? { status_freshness_ms: attributes.statusFreshnessMs } : {}),
    },
  });
}

export function trackDynatraceChargerStatusUpdated(options: {
  chargerId: string;
  status: string;
  source: DynatraceLiveStatusSource;
  scheduleStatus?: DynatraceScheduleStatus;
  powerKw?: number | null;
  statusFreshnessMs?: number;
  statusUpdateIntervalMs?: number;
  upstreamLatencyMs?: number;
}) {
  trackDynatraceEvent({
    journeyId: 'live_status_awareness',
    journeyStep: 'status',
    eventName: 'charger_status_updated',
    attributes: {
      ...getLiveStatusAttributes(options.chargerId),
      status: options.status,
      source: options.source,
      ...(options.scheduleStatus?.isScheduledChargeActive != null
        ? { scheduled_charge_active: options.scheduleStatus.isScheduledChargeActive }
        : {}),
      ...(options.scheduleStatus?.scheduledChargeStartTime
        ? { scheduled_charge_start_time: options.scheduleStatus.scheduledChargeStartTime }
        : {}),
      ...(options.scheduleStatus?.scheduledChargeEndTime
        ? { scheduled_charge_end_time: options.scheduleStatus.scheduledChargeEndTime }
        : {}),
      ...(options.scheduleStatus?.controlMode ? { control_mode: options.scheduleStatus.controlMode } : {}),
      ...(options.powerKw != null ? { power_kw: options.powerKw } : {}),
      ...(options.statusFreshnessMs !== undefined ? { status_freshness_ms: options.statusFreshnessMs } : {}),
      ...(options.statusUpdateIntervalMs !== undefined
        ? { status_update_interval_ms: options.statusUpdateIntervalMs }
        : {}),
      ...(options.upstreamLatencyMs !== undefined ? { upstream_to_client_latency_ms: options.upstreamLatencyMs } : {}),
    },
  });
}

export function trackDynatraceStatusPollResult(
  outcome: 'success' | 'failure',
  chargerId: string,
  statusFreshnessMs?: number
) {
  trackDynatraceEvent({
    journeyId: 'live_status_awareness',
    journeyStep: 'status',
    eventName: 'native_status_poll_result',
    outcome,
    attributes: {
      ...getLiveStatusAttributes(chargerId),
      ...(statusFreshnessMs !== undefined ? { status_freshness_ms: statusFreshnessMs } : {}),
    },
  });
}

export function trackDynatraceSessionMetrics(options: {
  chargerId: string;
  source: DynatraceLiveStatusSource;
  status?: string;
  scheduleStatus?: DynatraceScheduleStatus;
  sessionStartedAt?: string | null;
  sessionEndedAt?: string | null;
  powerKw?: number | null;
  energyKwh?: number | null;
  costAmount?: number | null;
  currency?: string | null;
}) {
  trackDynatraceEvent({
    journeyId: 'live_status_awareness',
    journeyStep: 'session',
    eventName: 'session_metrics_updated',
    attributes: {
      ...getLiveStatusAttributes(options.chargerId),
      source: options.source,
      ...(options.status ? { status: options.status } : {}),
      ...(options.scheduleStatus?.isScheduledChargeActive != null
        ? { scheduled_charge_active: options.scheduleStatus.isScheduledChargeActive }
        : {}),
      ...(options.scheduleStatus?.scheduledChargeStartTime
        ? { scheduled_charge_start_time: options.scheduleStatus.scheduledChargeStartTime }
        : {}),
      ...(options.scheduleStatus?.scheduledChargeEndTime
        ? { scheduled_charge_end_time: options.scheduleStatus.scheduledChargeEndTime }
        : {}),
      ...(options.scheduleStatus?.controlMode ? { control_mode: options.scheduleStatus.controlMode } : {}),
      ...(options.sessionStartedAt ? { session_started_at: options.sessionStartedAt } : {}),
      ...(options.sessionEndedAt ? { session_ended_at: options.sessionEndedAt } : {}),
      ...(options.powerKw != null ? { power_kw: options.powerKw } : {}),
      ...(options.energyKwh != null ? { energy_kwh: options.energyKwh } : {}),
      ...(options.costAmount != null ? { cost_amount: options.costAmount } : {}),
      ...(options.currency ? { currency: options.currency } : {}),
    },
  });
}

export function trackDynatraceConsentArrayResponseReceived(
  method: 'refreshConsent' | 'showConsentScreen' | 'showConsentFromSettings',
  purposes: { id: string; label: string; isSelected: boolean }[]
) {
  trackDynatraceEvent({
    journeyId: 'consent_management',
    journeyStep: 'account',
    eventName: 'consent_array_response_received',
    attributes: {
      consent_method: method,
      consent_response: JSON.stringify(purposes.map(({ id, label, isSelected }) => ({ id, label, isSelected }))),
    },
  });
}

export const trackDynatraceConsentManagementClosed = createDynatraceEventTracker({
  journeyId: 'consent_management',
  journeyStep: 'account',
  eventName: 'consent_management_closed',
});

export function trackDynatraceAnalyticsConsentConfirmed(source: ConsentEventSource) {
  trackDynatraceEvent({
    journeyId: 'consent_management',
    journeyStep: 'account',
    eventName: 'analytics_consent_confirmed',
    attributes: { consent_source: source },
  });
}

export function trackDynatraceTrackingEnabledContext(context: { userId: number; userRole: string; siteId?: number }) {
  trackDynatraceEvent({
    journeyId: 'customer_authentication',
    journeyStep: 'authorization',
    eventName: 'tracking_enabled_context',
    attributes: {
      environment: ENV_TYPE_SHORTHAND.toLowerCase(),
      user_id: context.userId,
      user_role: context.userRole,
      site_id: context.siteId ?? 'unknown',
    },
  });
}

export type AuthenticationFlowSource = 'login' | 'app_launch';

const AUTHENTICATION_FLOW_SOURCE_KEY = 'sc_dynatrace_customer_authentication_source';
const AUTHENTICATED_SESSION_EXPECTED_KEY = 'sc_dynatrace_authenticated_session_expected';
const AUTHENTICATION_LOGOUT_PENDING_KEY = 'sc_dynatrace_customer_logout_pending';

function getAuthenticationAttributes(source: AuthenticationFlowSource | 'active_session') {
  return {
    flow_source: source,
    identity_provider: 'EIMCharge',
    identity_layer: 'cognito_federation',
    region: REGION_SHORTHAND,
    aws_region: AWS_REGION,
    environment: ENV_TYPE_SHORTHAND.toLowerCase(),
  };
}

export function getDynatraceAuthenticationFlowSource(): AuthenticationFlowSource | undefined {
  try {
    const source = window.localStorage.getItem(AUTHENTICATION_FLOW_SOURCE_KEY);
    return source === 'login' || source === 'app_launch' ? source : undefined;
  } catch {
    return undefined;
  }
}

export function hasDynatraceAuthenticationAttempt() {
  return getDynatraceAuthenticationFlowSource() !== undefined;
}

export function hasDynatraceExpectedAuthenticationSession() {
  try {
    return window.localStorage.getItem(AUTHENTICATED_SESSION_EXPECTED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function hasDynatracePendingLogout() {
  try {
    return window.localStorage.getItem(AUTHENTICATION_LOGOUT_PENDING_KEY) === 'true';
  } catch {
    return false;
  }
}

export function clearDynatraceAuthenticationJourney() {
  clearJourneyTimer('customer_authentication');
  setAuthenticationFlowSource(null);
}

function setAuthenticationFlowSource(source: AuthenticationFlowSource | null) {
  try {
    if (source) window.localStorage.setItem(AUTHENTICATION_FLOW_SOURCE_KEY, source);
    else window.localStorage.removeItem(AUTHENTICATION_FLOW_SOURCE_KEY);
  } catch {
    // Timing and flow attribution are best-effort when localStorage is unavailable.
  }
}

function setExpectedAuthenticationSession(expected: boolean) {
  try {
    if (expected) window.localStorage.setItem(AUTHENTICATED_SESSION_EXPECTED_KEY, 'true');
    else window.localStorage.removeItem(AUTHENTICATED_SESSION_EXPECTED_KEY);
  } catch {
    // Session attribution is best-effort when localStorage is unavailable.
  }
}

export function startDynatraceAuthenticationJourney(source: AuthenticationFlowSource) {
  if (source === 'login') setExpectedAuthenticationSession(false);
  startJourneyTimer('customer_authentication');
  setAuthenticationFlowSource(source);
  trackDynatraceEvent({
    journeyId: 'customer_authentication',
    journeyStep: 'authentication',
    eventName: source === 'login' ? 'sign_in_started' : 'session_restore_started',
    priority: 'critical',
    attributes: getAuthenticationAttributes(source),
  });
}

export function startDynatraceAuthenticationSessionCheck() {
  startJourneyTimer('customer_authentication');
  setAuthenticationFlowSource('app_launch');
}

export function trackDynatraceOAuthCallbackReceived() {
  const source = getDynatraceAuthenticationFlowSource() ?? 'login';
  trackDynatraceEvent({
    journeyId: 'customer_authentication',
    journeyStep: 'authentication',
    eventName: 'oauth_callback_received',
    priority: 'critical',
    attributes: getAuthenticationAttributes(source),
  });
}

export function trackDynatraceSignInCancelled() {
  trackDynatraceEvent({
    journeyId: 'customer_authentication',
    journeyStep: 'authentication',
    eventName: 'sign_in_result',
    outcome: 'failure',
    priority: 'critical',
    attributes: {
      ...getAuthenticationAttributes(getDynatraceAuthenticationFlowSource() ?? 'login'),
      failure_reason: 'user_cancelled',
    },
  });
  setExpectedAuthenticationSession(false);
  clearDynatraceAuthenticationJourney();
}

export function trackDynatraceCognitoSessionResult(
  outcome: 'success' | 'failure',
  reason?: 'session_unavailable' | 'external_idp_tokens_missing'
) {
  const source = getDynatraceAuthenticationFlowSource() ?? 'app_launch';
  if (outcome === 'success') setExpectedAuthenticationSession(true);

  trackDynatraceEvent({
    journeyId: 'customer_authentication',
    journeyStep: 'session',
    eventName: 'cognito_session_result',
    outcome,
    priority: 'critical',
    attributes: {
      ...getAuthenticationAttributes(source),
      ...(reason ? { failure_reason: reason } : {}),
    },
  });

  if (outcome === 'failure') {
    if (source === 'login') setExpectedAuthenticationSession(false);
    clearDynatraceAuthenticationJourney();
  }
}

export function trackDynatraceHeliosAuthorizationResult(
  outcome: 'success' | 'failure',
  options: {
    httpStatus?: number;
    requestMethod?: string;
    apiOperation?: 'user_profile' | 'eim_token_refresh' | 'other_protected_api';
    failureReason?: 'request_failed';
  } = {}
) {
  const source = getDynatraceAuthenticationFlowSource() ?? 'active_session';
  trackDynatraceEvent({
    journeyId: 'customer_authentication',
    journeyStep: 'authorization',
    eventName: 'helios_authorization_result',
    outcome,
    priority: 'critical',
    attributes: {
      ...getAuthenticationAttributes(source),
      authorization_layer: options.apiOperation === 'eim_token_refresh' ? 'eim_token_refresh' : 'helios_authorization',
      ...(options.apiOperation ? { api_operation: options.apiOperation } : {}),
      ...(options.httpStatus !== undefined ? { http_status_code: options.httpStatus } : {}),
      ...(options.requestMethod ? { request_method: options.requestMethod } : {}),
      ...(options.failureReason ? { failure_reason: options.failureReason } : {}),
    },
  });

  if (options.apiOperation === 'user_profile' && source !== 'active_session') {
    trackDynatraceEvent({
      journeyId: 'customer_authentication',
      journeyStep: source === 'login' ? 'authentication' : 'session',
      eventName: source === 'login' ? 'sign_in_result' : 'session_restore_result',
      outcome,
      priority: 'critical',
      attributes: {
        ...getAuthenticationAttributes(source),
        authorization_layer: 'helios_authorization',
        request_method: options.requestMethod ?? 'GET',
        ...(options.httpStatus !== undefined ? { http_status_code: options.httpStatus } : {}),
        ...(options.failureReason ? { failure_reason: options.failureReason } : {}),
      },
    });
  }

  if (options.apiOperation === 'user_profile') clearDynatraceAuthenticationJourney();
}

export function trackDynatraceAuthenticationFailure(errorCode: string, failureStage: string, oauthErrorCode?: string) {
  const source = getDynatraceAuthenticationFlowSource() ?? 'login';
  trackDynatraceError({
    journeyId: 'customer_authentication',
    journeyStep: failureStage === 'authorization' ? 'authorization' : 'authentication',
    eventName: 'authentication_failed',
    errorCode,
    priority: 'critical',
    attributes: {
      ...getAuthenticationAttributes(source),
      failure_stage: failureStage,
      ...(failureStage === 'oauth_callback' && oauthErrorCode
        ? { oauth_error_code: normalizeOAuthCallbackErrorCode(oauthErrorCode) }
        : {}),
    },
  });
  if (source === 'login') setExpectedAuthenticationSession(false);
  clearDynatraceAuthenticationJourney();
}

export function trackDynatraceAuthorizationRejected(
  requestMethod: string,
  httpStatus: number,
  apiOperation: 'user_profile' | 'eim_token_refresh' | 'other_protected_api'
) {
  trackDynatraceHeliosAuthorizationResult('failure', { requestMethod, httpStatus, apiOperation });
}

export function trackDynatraceEimTokenRefreshResult(outcome: 'success' | 'failure', httpStatus?: number) {
  const source = getDynatraceAuthenticationFlowSource() ?? 'active_session';
  trackDynatraceEvent({
    journeyId: 'customer_authentication',
    journeyStep: 'authorization',
    eventName: 'eim_token_refresh_result',
    outcome,
    priority: 'critical',
    attributes: {
      ...getAuthenticationAttributes(source),
      authorization_layer: 'eim_token_refresh',
      api_operation: 'eim_token_refresh',
      ...(httpStatus !== undefined ? { http_status_code: httpStatus } : {}),
    },
  });
}

export function trackDynatraceLogoutStarted() {
  clearJourneyTimer('customer_authentication');
  setAuthenticationFlowSource(null);
  setExpectedAuthenticationSession(false);
  try {
    window.localStorage.setItem(AUTHENTICATION_LOGOUT_PENDING_KEY, 'true');
  } catch {
    // Logout attribution is best-effort when localStorage is unavailable.
  }
  startJourneyTimer('customer_authentication');
  trackDynatraceEvent({
    journeyId: 'customer_authentication',
    journeyStep: 'session',
    eventName: 'logout_started',
    priority: 'critical',
    attributes: getAuthenticationAttributes('active_session'),
  });
}

export type DynatraceLogoutStage =
  | 'idp_logout_url_resolution'
  | 'cognito_signout'
  | 'local_session_reset'
  | 'cognito_logout_redirect'
  | 'eim_logout_request'
  | 'native_logout_browser';

export type DynatraceLogoutFailureReason =
  | 'idp_logout_url_unavailable'
  | 'cognito_signout_failed'
  | 'browser_logout_timeout'
  | 'browser_open_failed'
  | 'browser_close_failed'
  | 'idp_logout_request_failed'
  | 'idp_logout_url_missing';

export function trackDynatraceLogoutStageResult(
  stage: DynatraceLogoutStage,
  outcome: 'success' | 'failure',
  failureReason?: DynatraceLogoutFailureReason
) {
  trackDynatraceEvent({
    journeyId: 'customer_authentication',
    journeyStep: 'session',
    eventName: 'logout_stage_result',
    outcome,
    priority: 'critical',
    attributes: {
      ...getAuthenticationAttributes('active_session'),
      logout_stage: stage,
      ...(failureReason ? { failure_reason: failureReason } : {}),
    },
  });
}

export function trackDynatraceLogoutResult(outcome: 'success' | 'failure', failureStage?: DynatraceLogoutStage) {
  trackDynatraceEvent({
    journeyId: 'customer_authentication',
    journeyStep: 'session',
    eventName: 'logout_result',
    outcome,
    priority: 'critical',
    attributes: {
      ...getAuthenticationAttributes('active_session'),
      ...(failureStage ? { failure_stage: failureStage } : {}),
    },
  });
  clearJourneyTimer('customer_authentication');
  try {
    window.localStorage.removeItem(AUTHENTICATION_LOGOUT_PENDING_KEY);
  } catch {
    // no-op
  }
}

// Call this whenever a fresh run through the pre-onboarding flow begins (e.g. LocationInfo mount)
// or on logout, so a stale timestamp from an abandoned/previous attempt can't leak into events
// that fire before trackDynatraceEvseOnboardingStarted() runs again (e.g. permission prompts).
export function resetOnboardingFunnelTimer() {
  clearJourneyTimer('onboarding');
}

// Call this from any onboarding entry point that doesn't go through trackDynatraceEvseOnboardingStarted()
// (e.g. NoDevice's "Add Charger" → device-scan, which skips DeviceRegister/LocationInfo entirely) so
// elapsed_ms_since_start reflects that entry's own attempt instead of a stale timer from long ago.
export function ensureOnboardingFunnelTimerStarted() {
  ensureJourneyTimerStarted('onboarding');
}

export function trackDynatraceEvseOnboardingStarted() {
  startJourneyTimer('onboarding');
  trackDynatraceEvent({
    journeyId: 'onboarding',
    journeyStep: 'enrollment',
    eventName: 'evse_onboarding_started',
    priority: 'critical',
  });
}

export const trackDynatraceUserRegistered = createDynatraceEventTracker({
  journeyId: 'onboarding',
  journeyStep: 'account',
  eventName: 'user_registered',
  priority: 'critical',
});

export function trackDynatraceLocationPermissionOutcome(outcome: PermissionOutcome) {
  trackDynatraceEvent({
    journeyId: 'onboarding',
    journeyStep: 'permission',
    eventName: 'location_permission_result',
    outcome: outcome === 'granted' ? 'success' : 'failure',
    priority: 'critical',
    attributes: { permission_type: 'location', permission_outcome: outcome },
  });
}

export function trackDynatraceCameraAccessOutcome(outcome: PermissionOutcome) {
  trackDynatraceEvent({
    journeyId: 'onboarding',
    journeyStep: 'scan',
    eventName: 'camera_access_result',
    outcome: outcome === 'granted' ? 'success' : 'failure',
    priority: 'critical',
    attributes: { permission_type: 'camera', permission_outcome: outcome },
  });
}

export const trackDynatraceConnectedToChargerWifi = createDynatraceEventTracker({
  journeyId: 'onboarding',
  journeyStep: 'wifi',
  eventName: 'connected_to_charger_wifi',
  priority: 'critical',
});

export function trackDynatraceReceivedNetworkList(networkCount: number, isRecovery = false) {
  trackDynatraceEvent({
    journeyId: 'onboarding',
    journeyStep: isRecovery ? 'wifi_recovery' : 'wifi',
    eventName: 'received_network_list',
    attributes: { network_count: networkCount },
  });
}

// Fires with journeyStep 'wifi_recovery' when the user reconnects after having failed to set Wi-Fi
// during onboarding (isEditing) - a separate stream from the primary onboarding funnel above, since
// the charger may have already been fully onboarded by the time this runs.
export function trackDynatraceUserOfflineSetupError(errorMessage = 'offline_setup_error', isRecovery = false) {
  trackDynatraceError({
    journeyId: 'onboarding',
    journeyStep: isRecovery ? 'wifi_recovery' : 'enrollment',
    eventName: 'user_offline_setup_error',
    errorCode: 'offline_setup_error',
    errorMessage,
    priority: 'critical',
  });
}

export function trackDynatraceAddEvseToSiteSuccess(siteId: number | string, siteDeviceId: number | string) {
  trackDynatraceEvent({
    journeyId: 'onboarding',
    journeyStep: 'add_to_site',
    eventName: 'add_evse_to_site',
    priority: 'critical',
    attributes: { site_id: siteId, site_device_id: siteDeviceId },
  });
}

export function trackDynatraceAddEvseToSiteError(
  errorMessage: string,
  siteId?: number | string,
  attemptNumber?: number
) {
  trackDynatraceError({
    journeyId: 'onboarding',
    journeyStep: 'add_to_site',
    eventName: 'add_evse_to_site',
    errorCode: 'add_evse_to_site_failed',
    errorMessage,
    priority: 'critical',
    attributes: {
      ...(siteId !== undefined ? { site_id: siteId } : {}),
      ...(attemptNumber !== undefined ? { attempt_number: attemptNumber } : {}),
    },
  });
}

export function trackDynatraceLocalAuthResult(
  outcome: 'success' | 'failure',
  attemptNumber?: number,
  isRecovery = false
) {
  trackDynatraceEvent({
    journeyId: 'onboarding',
    journeyStep: isRecovery ? 'wifi_recovery' : 'local_auth',
    eventName: 'local_auth_result',
    outcome,
    priority: 'critical',
    attributes: attemptNumber !== undefined ? { attempt_number: attemptNumber } : undefined,
  });
}

export function trackDynatraceWifiAppliedResult(
  outcome: 'success' | 'failure',
  attemptNumber?: number,
  isRecovery = false
) {
  trackDynatraceEvent({
    journeyId: 'onboarding',
    journeyStep: isRecovery ? 'wifi_recovery' : 'wifi',
    eventName: 'wifi_applied_result',
    outcome,
    priority: 'critical',
    attributes: attemptNumber !== undefined ? { attempt_number: attemptNumber } : undefined,
  });
}

// `failure` means the charger answered but its model number did not resolve to a known variant.
export function trackDynatraceEvseVariantDetected(
  evseVariant: EvseVariant,
  modelNumber: string | null,
  isRecovery = false
) {
  trackDynatraceEvent({
    journeyId: 'onboarding',
    journeyStep: isRecovery ? 'wifi_recovery' : 'wifi',
    eventName: 'evse_variant_detected',
    outcome: evseVariant === 'Unknown' ? 'failure' : 'success',
    priority: 'critical',
    attributes: { evse_variant: evseVariant, model_number: modelNumber?.slice(0, 40) ?? 'none' },
  });
}

export function trackDynatraceEvseVariantDetectionError(errorMessage: string, isRecovery = false) {
  trackDynatraceError({
    journeyId: 'onboarding',
    journeyStep: isRecovery ? 'wifi_recovery' : 'wifi',
    eventName: 'evse_variant_detected',
    errorCode: 'evse_variant_detection_failed',
    errorMessage,
    priority: 'critical',
  });
}

export function trackDynatraceEvseModelSyncError(errorMessage: string, siteDeviceId: number | string) {
  trackDynatraceError({
    journeyId: 'onboarding',
    journeyStep: 'add_to_site',
    eventName: 'evse_model_sync',
    errorCode: 'evse_model_sync_failed',
    errorMessage,
    priority: 'critical',
    attributes: { site_device_id: siteDeviceId },
  });
}

export function trackDynatraceChargerAddedSuccessfully() {
  trackDynatraceEvent({
    journeyId: 'onboarding',
    journeyStep: 'enrollment',
    eventName: 'charger_added_successfully',
    priority: 'critical',
  });
  clearJourneyTimer('onboarding');
}

// The 'wifi_recovery' counterpart to trackDynatraceChargerAddedSuccessfully, for a user fixing Wi-Fi
// via Settings after onboarding rather than during it - deliberately doesn't touch the onboarding
// timer, since that funnel has already completed (or timed out) by the time this flow runs.
export const trackDynatraceWifiRecoveryCompleted = createDynatraceEventTracker({
  journeyId: 'onboarding',
  journeyStep: 'wifi_recovery',
  eventName: 'wifi_recovery_completed',
  priority: 'critical',
});

export const trackDynatraceTariffSetupStarted = createDynatraceEventTracker({
  journeyId: 'tariffs_setup',
  journeyStep: 'tariff',
  eventName: 'tariff_setup_started',
});

export const trackDynatraceTariffSkipped = createDynatraceEventTracker({
  journeyId: 'tariffs_setup',
  journeyStep: 'tariff',
  eventName: 'tariff_skipped',
});

export function trackDynatraceTariffImportedFromWattbuy() {
  trackDynatraceEvent({
    journeyId: 'tariffs_setup',
    journeyStep: 'tariff',
    eventName: 'tariff_imported_from_wattbuy',
    attributes: { action: 'save', tariff_source: 'wattbuy' },
  });
}

export function trackDynatraceTariffSetManually() {
  trackDynatraceEvent({
    journeyId: 'tariffs_setup',
    journeyStep: 'tariff',
    eventName: 'tariff_set_manually',
    attributes: { action: 'save', tariff_source: 'manual' },
  });
}

export const trackDynatraceScheduleSetupStarted = createDynatraceEventTracker({
  journeyId: 'schedule_setup',
  journeyStep: 'schedule',
  eventName: 'schedule_setup_started',
});

export const trackDynatraceScheduleSkipped = createDynatraceEventTracker({
  journeyId: 'schedule_setup',
  journeyStep: 'schedule',
  eventName: 'schedule_skipped',
});

export const trackDynatraceScheduleCreated = createDynatraceEventTracker({
  journeyId: 'schedule_setup',
  journeyStep: 'schedule',
  eventName: 'schedule_created',
});

export const trackDynatraceScheduleConfigError = createDynatraceErrorTracker({
  journeyId: 'schedule_setup',
  journeyStep: 'schedule',
  eventName: 'schedule_config_error',
  errorCode: 'schedule_config_error',
});

export type DynatraceScheduleOperation = 'read' | 'create' | 'update' | 'delete';
export type DynatraceScheduleInput = {
  name?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  daysOfWeek?: number[];
};

export function trackDynatraceScheduleOperationResult(options: {
  operation: DynatraceScheduleOperation;
  outcome: 'success' | 'failure';
  chargerId: string;
  startedAt: number;
  scheduleId?: string;
  scheduleInput?: DynatraceScheduleInput;
}) {
  const scheduleName = options.scheduleInput?.name?.trim();
  const daysOfWeek = options.scheduleInput?.daysOfWeek;
  const attributes = {
    charger_id: options.chargerId,
    operation: options.operation,
    request_duration_ms: Math.max(0, Date.now() - options.startedAt),
    ...(options.scheduleId ? { schedule_id: options.scheduleId } : {}),
    ...(scheduleName !== undefined
      ? { schedule_name_present: scheduleName.length > 0, schedule_name_length: scheduleName.length }
      : {}),
    ...(options.scheduleInput?.startTime ? { schedule_start_time: options.scheduleInput.startTime } : {}),
    ...(options.scheduleInput?.endTime ? { schedule_end_time: options.scheduleInput.endTime } : {}),
    ...(daysOfWeek ? { schedule_days_of_week: [...daysOfWeek].sort((left, right) => left - right).join(',') } : {}),
  };

  if (options.outcome === 'failure') {
    trackDynatraceError({
      journeyId: 'schedule_setup',
      journeyStep: 'schedule',
      eventName: 'schedule_operation_failed',
      errorCode: 'schedule_api_failed',
      attributes,
    });
    return;
  }

  trackDynatraceEvent({
    journeyId: 'schedule_setup',
    journeyStep: 'schedule',
    eventName: 'schedule_operation_succeeded',
    attributes,
  });
}

export const trackDynatraceChargerControlOpened = createDynatraceEventTracker({
  journeyId: 'remote_control',
  journeyStep: 'control',
  eventName: 'charger_control_opened',
});

export const trackDynatraceChargingStopped = createDynatraceEventTracker({
  journeyId: 'remote_control',
  journeyStep: 'control',
  eventName: 'charging_stopped',
  priority: 'critical',
});

export const trackDynatraceChargingStoppedError = createDynatraceErrorTracker({
  journeyId: 'remote_control',
  journeyStep: 'control',
  eventName: 'charging_stopped',
  errorCode: 'stop_charging_failed',
  priority: 'critical',
});

export const trackDynatraceChargingForceStarted = createDynatraceEventTracker({
  journeyId: 'remote_control',
  journeyStep: 'control',
  eventName: 'charging_force_started',
  priority: 'critical',
});

export const trackDynatraceChargingForceStartedError = createDynatraceErrorTracker({
  journeyId: 'remote_control',
  journeyStep: 'control',
  eventName: 'charging_force_started',
  errorCode: 'start_charging_failed',
  priority: 'critical',
});

export const trackDynatraceCablePluggedOut = createDynatraceEventTracker({
  journeyId: 'live_status_awareness',
  journeyStep: 'status',
  eventName: 'cable_plugged_out',
  priority: 'critical',
});

export const trackDynatraceCablePluggedInWithoutSchedule = createDynatraceEventTracker({
  journeyId: 'live_status_awareness',
  journeyStep: 'status',
  eventName: 'cable_plugged_in_no_schedule',
  priority: 'critical',
});

export const trackDynatraceActivityOpened = createDynatraceEventTracker({
  journeyId: 'historical_data',
  journeyStep: 'activity',
  eventName: 'activity_opened',
});

export function trackDynatraceActivityTimeframeChanged(timeframe: string) {
  trackDynatraceEvent({
    journeyId: 'historical_data',
    journeyStep: 'activity',
    eventName: 'activity_timeframe_changed',
    attributes: { timeframe },
  });
}

export function trackDynatraceActivityDateNavigated(direction: 'forward' | 'back', dateRangeType: string) {
  trackDynatraceEvent({
    journeyId: 'historical_data',
    journeyStep: 'activity',
    eventName: 'activity_date_navigated',
    attributes: { direction, date_range_type: dateRangeType },
  });
}

export const trackDynatraceHamburgerMenuOpened = createDynatraceEventTracker({
  journeyId: 'hamburger_menu',
  journeyStep: 'menu',
  eventName: 'hamburger_menu_opened',
});

export const trackDynatraceChargerInfoOpened = createDynatraceEventTracker({
  journeyId: 'hamburger_menu',
  journeyStep: 'menu',
  eventName: 'charger_info_opened',
});

export const trackDynatraceWifiConfigOpened = createDynatraceEventTracker({
  journeyId: 'hamburger_menu',
  journeyStep: 'menu',
  eventName: 'wifi_config_opened',
});

export const trackDynatraceWifiChangeStarted = createDynatraceEventTracker({
  journeyId: 'hamburger_menu',
  journeyStep: 'menu',
  eventName: 'wifi_change_started',
});

export const trackDynatraceGuestShareRemoved = createDynatraceEventTracker({
  journeyId: 'hamburger_menu',
  journeyStep: 'menu',
  eventName: 'guest_share_removed',
});

export const trackDynatraceGuestShareAdded = createDynatraceEventTracker({
  journeyId: 'hamburger_menu',
  journeyStep: 'menu',
  eventName: 'guest_share_added',
});

export const trackDynatraceScheduleSettingsOpened = createDynatraceEventTracker({
  journeyId: 'hamburger_menu',
  journeyStep: 'menu',
  eventName: 'schedule_settings_opened',
});

export const trackDynatraceEnergyRatesOpened = createDynatraceEventTracker({
  journeyId: 'hamburger_menu',
  journeyStep: 'menu',
  eventName: 'energy_rates_opened',
});

export const trackDynatraceDisplayConfigOpened = createDynatraceEventTracker({
  journeyId: 'hamburger_menu',
  journeyStep: 'menu',
  eventName: 'display_config_opened',
});
